import { BLOCK } from '../data/constants';

/**
 * A display field's `body` carries its own structure, written by ops in the
 * config panel. One marker per line, everything else is prose:
 *
 *   ## Title | Badge   opens a card; the plain lines under it are its body
 *   >                  a highlighted note
 *   ?                  a question the client can expand
 *   -                  a bullet, belonging to the ? above it
 *   (blank line)       closes whatever block is open
 *
 * The badge is optional: "## Couple" alone is a card with no pill.
 */
const CARD = /^##\s*(.*)$/;
const NOTE = /^>\s*(.*)$/;
const HELP = /^\?\s*(.*)$/;
const ITEM = /^-\s*(.*)$/;

// Prose arrives line by line; consecutive lines read as one paragraph.
const join = (current, next) => (current ? `${current} ${next}` : next);

export const parseBody = (body) => {
  const blocks = [];
  let open = null;

  const close = () => {
    if (!open) return;
    // A marker with nothing under it is not a block.
    const empty =
      (open.type === BLOCK.PARAGRAPH && !open.text) ||
      (open.type === BLOCK.NOTE && !open.text) ||
      (open.type === BLOCK.CARD && !open.title) ||
      (open.type === BLOCK.HELP && !open.question);
    if (!empty) blocks.push(open);
    open = null;
  };

  String(body || '')
    .split('\n')
    .forEach((raw) => {
      const line = raw.trim();

      if (!line) {
        close();
        return;
      }

      const card = line.match(CARD);
      if (card) {
        close();
        const [title, badge] = card[1].split('|');
        open = {
          type: BLOCK.CARD,
          title: (title || '').trim(),
          badge: (badge || '').trim(),
          text: '',
        };
        return;
      }

      const note = line.match(NOTE);
      if (note) {
        close();
        open = { type: BLOCK.NOTE, text: note[1].trim() };
        return;
      }

      const help = line.match(HELP);
      if (help) {
        close();
        open = { type: BLOCK.HELP, question: help[1].trim(), intro: '', items: [] };
        return;
      }

      const item = line.match(ITEM);
      if (item && open && open.type === BLOCK.HELP) {
        open.items.push(item[1].trim());
        return;
      }

      // Plain prose: it belongs to whatever is open, or starts a paragraph.
      if (open && open.type === BLOCK.CARD) {
        open.text = join(open.text, line);
        return;
      }
      if (open && open.type === BLOCK.NOTE) {
        open.text = join(open.text, line);
        return;
      }
      if (open && open.type === BLOCK.HELP) {
        // Before the first bullet this is the intro; after it, the last item
        // is still being written.
        if (open.items.length) {
          open.items[open.items.length - 1] = join(
            open.items[open.items.length - 1],
            line
          );
        } else {
          open.intro = join(open.intro, line);
        }
        return;
      }
      if (!open) open = { type: BLOCK.PARAGRAPH, text: '' };
      open.text = join(open.text, line);
    });

  close();
  return blocks;
};
